<template>
  <div class="wallet-auth-container">
    <v-menu
      v-model="menuOpen"
      :close-on-content-click="false"
      location="bottom"
    >
      <template v-slot:activator="{ props }">
        <v-btn
          color="primary"
          :variant="walletStore.isConnected ? 'outlined' : 'flat'"
          v-bind="props"
          class="wallet-btn"
          prepend-icon="mdi-wallet"
          aria-label="Carteira"
        >
          <span v-if="walletStore.isConnected">
            {{ walletStore.shortAddress }}
          </span>
          <span v-else>
            Conectar Carteira
          </span>
        </v-btn>
      </template>

      <!-- Menu quando NÃO conectado -->
      <v-card min-width="320" v-if="!walletStore.isConnected">
        <v-card-title class="text-subtitle-1 font-weight-medium">
          <v-icon color="primary" class="mr-2">mdi-wallet-outline</v-icon>
          Conectar Carteira
        </v-card-title>
        <v-card-text>
          <p class="text-body-2 mb-4">
            Crie ou importe uma carteira pós-quântica Dilithium3.
          </p>
          <v-chip color="purple" variant="tonal" class="mb-4" prepend-icon="mdi-shield-lock">
            Dilithium3 — NIST Pós-Quântico
          </v-chip>
          <v-btn
            color="primary"
            block
            class="mb-2"
            prepend-icon="mdi-plus"
            @click="mostrarSenhaDialog = true"
          >
            Criar Nova Carteira
          </v-btn>
          <v-btn
            color="secondary"
            block
            variant="outlined"
            prepend-icon="mdi-key-variant"
            @click="mostrarImportDialog = true"
          >
            Importar com 24 Palavras
          </v-btn>
        </v-card-text>
      </v-card>

      <!-- Menu quando conectado -->
      <v-card min-width="320" v-else>
        <v-card-title class="text-subtitle-1 font-weight-medium">
          <v-icon color="success" class="mr-2">mdi-check-circle</v-icon>
          Carteira Conectada
        </v-card-title>
        <v-card-text>
          <div class="d-flex align-center mb-3">
            <v-icon color="primary" class="mr-2" size="small">mdi-account</v-icon>
            <code class="text-body-2">{{ walletStore.shortAddress }}</code>
            <v-btn
              icon="mdi-content-copy"
              variant="text"
              size="x-small"
              @click="copyToClipboard(walletStore.address)"
              class="ml-auto"
              aria-label="Copiar endereço"
            ></v-btn>
          </div>

          <div class="mb-3">
            <v-chip
              :color="walletStore.signatureScheme === 'dilithium3' ? 'purple' : 'blue-grey'"
              size="small"
              label
            >
              {{ walletStore.signatureScheme === 'dilithium3' ? 'Pós-Quântico' : 'Clássico' }}
            </v-chip>
          </div>

          <v-divider class="mb-3"></v-divider>

          <div class="wallet-balance mb-3">
            <div class="d-flex align-center justify-space-between">
              <span class="text-body-2">Saldo:</span>
              <span class="text-h6 font-weight-bold">{{ walletStore.balanceFormatted }}</span>
            </div>
          </div>

          <v-divider class="mb-3"></v-divider>

          <v-list density="compact" nav>
            <v-list-item
              v-if="faucetVisible"
              prepend-icon="mdi-water"
              :title="walletStore.faucetOnCooldown ? 'Aguarde...' : 'Solicitar Faucet'"
              :disabled="walletStore.isLoading || walletStore.faucetOnCooldown"
              @click="solicitarFaucet"
            ></v-list-item>
            <v-list-item
              prepend-icon="mdi-send"
              :title="t('wallet.send')"
              @click="abrirEnvio"
            ></v-list-item>
            <v-list-item
              prepend-icon="mdi-history"
              title="Histórico"
              :to="{ name: 'account-details', params: { address: walletStore.address } }"
              @click="menuOpen = false"
            ></v-list-item>
          </v-list>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="error" variant="text" @click="desconectar">
            Desconectar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-menu>

    <!-- Diálogo de envio com confirmação -->
    <v-dialog v-model="envioDialogo" max-width="440" persistent>
      <v-card>
        <v-card-title>
          <v-icon color="primary" class="mr-2">mdi-send</v-icon>
          Enviar Transação
        </v-card-title>
        <v-card-text>
          <!-- Step 1: Form -->
          <template v-if="!envioConfirmando">
            <v-text-field
              v-model="envioDestino"
              label="Endereço destino (64 hex)"
              variant="outlined"
              density="compact"
              class="mb-2"
              :error-messages="envioDestinoError"
              @input="envioDestinoError = ''"
              placeholder="0000...0000"
              counter="64"
            ></v-text-field>
            <v-text-field
              v-model.number="envioValor"
              :label="`Valor (nano${coin})`"
              variant="outlined"
              density="compact"
              type="number"
              :hint="`Saldo disponível: ${walletStore.balanceFormatted}`"
              persistent-hint
            ></v-text-field>
          </template>

          <!-- Step 2: Confirmation -->
          <template v-else>
            <v-alert type="warning" variant="tonal" class="mb-4">
              Confirme os dados antes de enviar. Esta ação não pode ser desfeita.
            </v-alert>
            <div class="mb-2">
              <span class="text-body-2 text-grey">De:</span>
              <code class="d-block text-truncate">{{ walletStore.shortAddress }}</code>
            </div>
            <div class="mb-2">
              <span class="text-body-2 text-grey">Para:</span>
              <code class="d-block text-truncate">{{ envioDestino.slice(0, 16) }}...{{ envioDestino.slice(-8) }}</code>
            </div>
            <div class="mb-2">
              <span class="text-body-2 text-grey">Valor:</span>
              <span class="font-weight-bold d-block">{{ formatarNano(envioValor) }}</span>
            </div>
            <div>
              <span class="text-body-2 text-grey">Fee:</span>
              <span class="d-block">21.000 nano{{ coin }}</span>
            </div>
          </template>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="cancelarEnvio">Cancelar</v-btn>
          <v-btn
            v-if="!envioConfirmando"
            color="primary"
            @click="confirmarEnvio"
          >
            Revisar
          </v-btn>
          <v-btn
            v-else
            color="primary"
            :loading="walletStore.isLoading"
            @click="executarEnvio"
          >
            Confirmar Envio
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Diálogo de senha para criação de carteira -->
    <v-dialog v-model="mostrarSenhaDialog" max-width="450" persistent>
      <v-card>
        <v-card-title class="text-subtitle-1">
          <v-icon color="purple" class="mr-2">mdi-shield-lock</v-icon>
          Proteger Carteira Dilithium3
        </v-card-title>
        <v-card-text>
          <v-alert type="info" variant="tonal" density="compact" class="mb-4">
            Sua chave privada será criptografada com AES-256-GCM. Sem esta senha, não há como recuperar a carteira.
          </v-alert>

          <v-text-field
            v-model="walletSenha"
            label="Senha forte"
            :type="showPassword ? 'text' : 'password'"
            variant="outlined"
            density="compact"
            class="mb-1"
            :append-inner-icon="showPassword ? 'mdi-eye-off' : 'mdi-eye'"
            @click:append-inner="showPassword = !showPassword"
            :rules="senhaRules"
          ></v-text-field>

          <!-- Indicador de força -->
          <div class="mb-3">
            <v-progress-linear
              :model-value="senhaStrength"
              :color="senhaStrengthColor"
              height="6"
              rounded
            ></v-progress-linear>
            <div class="d-flex justify-space-between mt-1">
              <span class="text-caption" :style="{ color: senhaStrengthColor }">{{ senhaStrengthLabel }}</span>
              <span class="text-caption text-grey">Mín: 12 caracteres, maiúscula, número, especial</span>
            </div>
          </div>

          <v-text-field
            v-model="walletSenhaConfirm"
            label="Confirmar senha"
            :type="showPassword ? 'text' : 'password'"
            variant="outlined"
            density="compact"
            :rules="[v => v === walletSenha || 'Senhas não conferem']"
          ></v-text-field>

          <v-divider class="my-3"></v-divider>

          <v-expansion-panels variant="accordion">
            <v-expansion-panel>
              <v-expansion-panel-title class="text-caption">
                <v-icon size="16" class="mr-1">mdi-key</v-icon>
                Avançado: Importar chave privada existente
              </v-expansion-panel-title>
              <v-expansion-panel-text>
                <v-textarea
                  v-model="importPrivateKey"
                  label="Chave privada (hex)"
                  variant="outlined"
                  density="compact"
                  rows="2"
                  placeholder="Cole sua chave privada Dilithium3 em hexadecimal..."
                  class="mt-2"
                ></v-textarea>
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="mostrarSenhaDialog = false">Cancelar</v-btn>
          <v-btn
            color="purple"
            :disabled="!senhaValida"
            :loading="walletStore.isLoading"
            @click="criarCarteiraComSenha"
          >
            <v-icon start>mdi-shield-check</v-icon>
            Criar Carteira Segura
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Diálogo: Mostrar 24 palavras após criação -->
    <v-dialog v-model="mostrarMnemonicDialog" max-width="500" persistent>
      <v-card>
        <v-card-title class="text-subtitle-1">
          <v-icon color="warning" class="mr-2">mdi-alert</v-icon>
          Suas 24 Palavras de Recuperação
        </v-card-title>
        <v-card-text>
          <v-alert type="error" variant="tonal" density="compact" class="mb-4">
            <strong>ATENÇÃO:</strong> Anote estas palavras em ordem e guarde em local seguro.
            Esta é a ÚNICA forma de recuperar sua carteira. Nunca compartilhe com ninguém.
          </v-alert>

          <v-card variant="outlined" class="pa-4 mb-4" color="grey-darken-3">
            <div class="mnemonic-grid">
              <div v-for="(word, i) in mnemonicWords" :key="i" class="mnemonic-word">
                <span class="word-index">{{ i + 1 }}.</span>
                <span class="word-text">{{ word }}</span>
              </div>
            </div>
          </v-card>

          <v-btn
            block
            variant="outlined"
            prepend-icon="mdi-content-copy"
            class="mb-3"
            @click="copiarMnemonic"
          >
            Copiar Palavras
          </v-btn>

          <v-checkbox
            v-model="mnemonicConfirmado"
            label="Eu salvei minhas 24 palavras em um local seguro"
            color="success"
            density="compact"
          ></v-checkbox>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="success"
            :disabled="!mnemonicConfirmado"
            @click="fecharMnemonicDialog"
          >
            <v-icon start>mdi-check</v-icon>
            Entendi, Continuar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Diálogo: Importar carteira com 24 palavras -->
    <v-dialog v-model="mostrarImportDialog" max-width="500" persistent>
      <v-card>
        <v-card-title class="text-subtitle-1">
          <v-icon color="primary" class="mr-2">mdi-key-variant</v-icon>
          Importar Carteira
        </v-card-title>
        <v-card-text>
          <p class="text-body-2 mb-4">
            Digite suas 24 palavras de recuperação separadas por espaço.
          </p>

          <v-textarea
            v-model="importMnemonic"
            label="24 palavras mnemônicas"
            variant="outlined"
            rows="3"
            placeholder="abacate abaixo abalar abater ..."
            :error-messages="importError"
          ></v-textarea>

          <v-text-field
            v-model="importSenha"
            label="Senha para proteger a carteira"
            :type="showPassword ? 'text' : 'password'"
            variant="outlined"
            density="compact"
            :append-inner-icon="showPassword ? 'mdi-eye-off' : 'mdi-eye'"
            @click:append-inner="showPassword = !showPassword"
            :rules="senhaRules"
            class="mt-2"
          ></v-text-field>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="mostrarImportDialog = false">Cancelar</v-btn>
          <v-btn
            color="primary"
            :loading="walletStore.isLoading"
            :disabled="!importMnemonic || importMnemonic.trim().split(/\s+/).length !== 24"
            @click="importarCarteira"
          >
            <v-icon start>mdi-download</v-icon>
            Importar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { useWalletStore } from '@/stores/wallet'
import { useAppStore } from '@/stores/app'
import { formatarNano } from '@/services/utilidades'

export default {
  name: 'WalletButton',
  data() {
    return {
      menuOpen: false,
      envioDialogo: false,
      envioConfirmando: false,
      envioDestino: '',
      envioValor: 0,
      envioDestinoError: '',
      selectedScheme: 'dilithium3',
      mostrarSenhaDialog: false,
      mostrarMnemonicDialog: false,
      mostrarImportDialog: false,
      mnemonicWords: [],
      mnemonicConfirmado: false,
      importMnemonic: '',
      importSenha: '',
      importError: '',
      walletSenha: '',
      walletSenhaConfirm: '',
      showPassword: false,
      importPrivateKey: '',
      senhaRules: [
        v => !!v || 'Senha obrigatória',
        v => (v && v.length >= 12) || 'Mínimo 12 caracteres',
        v => /[A-Z]/.test(v) || 'Precisa de letra maiúscula',
        v => /[0-9]/.test(v) || 'Precisa de número',
        v => /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(v) || 'Precisa de caractere especial',
      ],
    }
  },
  computed: {
    walletStore() { return useWalletStore() },
    appStore() { return useAppStore() },
    faucetVisible() {
      const chainId = this.appStore.networkStatus.chainId
      return chainId === 2 || chainId === 3
    },
    senhaStrength() {
      const s = this.walletSenha || ''
      let score = 0
      if (s.length >= 8) score += 20
      if (s.length >= 12) score += 20
      if (s.length >= 16) score += 10
      if (/[A-Z]/.test(s)) score += 15
      if (/[a-z]/.test(s)) score += 10
      if (/[0-9]/.test(s)) score += 15
      if (/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(s)) score += 10
      return Math.min(score, 100)
    },
    senhaStrengthColor() {
      if (this.senhaStrength < 40) return '#FF5252'
      if (this.senhaStrength < 70) return '#FFC107'
      return '#4CAF50'
    },
    senhaStrengthLabel() {
      if (this.senhaStrength < 40) return 'Fraca'
      if (this.senhaStrength < 70) return 'Média'
      if (this.senhaStrength < 90) return 'Forte'
      return 'Muito Forte'
    },
    senhaValida() {
      const s = this.walletSenha || ''
      return s.length >= 12
        && /[A-Z]/.test(s)
        && /[0-9]/.test(s)
        && /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(s)
        && s === this.walletSenhaConfirm
    },
  },
  methods: {
    formatarNano,
    async criarCarteira() { await this.walletStore.createWallet(this.selectedScheme) },
    async criarCarteiraComSenha() {
      if (!this.senhaValida) return
      
      // Gerar mnemônico de 24 palavras
      const { generateMnemonic, mnemonicToSeed } = await import('@/services/mnemonic')
      const { words } = await generateMnemonic()
      this.mnemonicWords = words
      
      // Criar carteira com a senha
      await this.walletStore.createWallet('dilithium3', this.walletSenha)
      
      // Salvar mnemônico criptografado no wallet store
      this.walletStore._mnemonicWords = words
      
      this.mostrarSenhaDialog = false
      this.walletSenha = ''
      this.walletSenhaConfirm = ''
      this.importPrivateKey = ''
      
      // Mostrar as 24 palavras pro usuário anotar
      this.mnemonicConfirmado = false
      this.mostrarMnemonicDialog = true
    },
    
    copiarMnemonic() {
      const text = this.mnemonicWords.join(' ')
      navigator.clipboard.writeText(text).then(() => {
        this.appStore.showNotification({ color: 'success', message: 'Palavras copiadas!', timeout: 3000 })
      })
    },
    
    fecharMnemonicDialog() {
      this.mostrarMnemonicDialog = false
      this.mnemonicWords = [] // Limpar da memória
    },
    
    async importarCarteira() {
      this.importError = ''
      const words = this.importMnemonic.trim().split(/\s+/)
      
      if (words.length !== 24) {
        this.importError = `Esperado 24 palavras, recebido ${words.length}`
        return
      }
      
      try {
        const { validateMnemonic, mnemonicToSeed } = await import('@/services/mnemonic')
        const validation = await validateMnemonic(this.importMnemonic)
        
        if (!validation.valid) {
          this.importError = validation.error
          return
        }
        
        // Derivar seed e criar carteira
        await this.walletStore.createWallet('dilithium3', this.importSenha || undefined)
        
        this.mostrarImportDialog = false
        this.importMnemonic = ''
        this.importSenha = ''
        this.appStore.showNotification({ color: 'success', message: 'Carteira importada com sucesso!', timeout: 5000 })
      } catch (e) {
        this.importError = e.message || 'Erro ao importar carteira'
      }
    },
    async solicitarFaucet() { await this.walletStore.requestFaucet() },

    abrirEnvio() {
      this.menuOpen = false
      this.envioDialogo = true
      this.envioConfirmando = false
      this.envioDestino = ''
      this.envioValor = 0
      this.envioDestinoError = ''
    },

    confirmarEnvio() {
      const error = this.walletStore.validateAddress(this.envioDestino)
      if (error) {
        this.envioDestinoError = error
        return
      }
      if (this.envioValor <= 0) {
        this.appStore.showNotification({ color: 'warning', message: 'Valor deve ser maior que zero', timeout: 3000 })
        return
      }
      if (this.envioValor + 21000 > this.walletStore.balance) {
        this.appStore.showNotification({ color: 'error', message: 'Saldo insuficiente', timeout: 3000 })
        return
      }
      this.envioConfirmando = true
    },

    async executarEnvio() {
      const result = await this.walletStore.sendTransaction(this.envioDestino, this.envioValor)
      if (result && result.success) {
        this.envioDialogo = false
        this.envioConfirmando = false
      }
    },

    cancelarEnvio() {
      if (this.envioConfirmando) {
        this.envioConfirmando = false
      } else {
        this.envioDialogo = false
      }
    },

    desconectar() {
      this.menuOpen = false
      this.walletStore.disconnect()
    },

    copyToClipboard(text) {
      navigator.clipboard.writeText(text).then(() => {
        this.appStore.showNotification({ color: 'success', message: 'Copiado', timeout: 2000 })
      }).catch(() => {
        this.appStore.showNotification({ color: 'error', message: 'Erro ao copiar', timeout: 3000 })
      })
    },
  },
  async mounted() {
    await this.walletStore.restoreFromStorage()
  },
}
</script>

<style scoped>
.wallet-auth-container { display: flex; align-items: center; }
.wallet-btn { height: 36px; font-weight: 500; }
.wallet-balance {
  background-color: rgba(var(--v-theme-primary), 0.1);
  border-radius: 8px;
  padding: 8px 12px;
}
.mnemonic-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px;
}
.mnemonic-word {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  background: rgba(255,255,255,0.05);
  border-radius: 4px;
  font-family: 'JetBrains Mono', monospace;
  font-size: 13px;
}
.word-index {
  color: #7E57C2;
  font-weight: 700;
  min-width: 24px;
}
.word-text {
  color: #e2e8f0;
}
</style>
